import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import '../../styles/full-calendar.css';
import { CalendarEvents, centralAPI } from '../../services/central-api';
import moment from 'moment';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import Toast from 'react-native-toast-message';
import { translate, getDefaultLanguage } from '../../services/translate';
import MyAppText from '../../components/MyAppText';
import CustomButton from '../../components/CustomButton';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { WindowInformation } from '../../services/window-information';
import { titleCasePt } from '../../services/title-case-pt';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Card from '../../components/Card';


export default function Calendar() {
    const { styles } = useStyles(stylesheet);
    const [calendarEvents, setCalendarEvents] = useState<CalendarEvents>();
    const calendarRef = useRef<FullCalendar | null>(null);
    const [startDate, setStartDate] = useState<number>(0);
    const [endDate, setEndDate] = useState<number>(0);
    const [title, setTitle] = useState('');
    const [currentView, setCurrentView] = useState<'dayGridMonth' | 'timeGridWeek' | 'timeGridDay'>('timeGridWeek');

    const windowInfo = WindowInformation();

    function updateTitle() {
        const api = calendarRef.current?.getApi();
        if (!api) {
            return;
        }
        setTitle(api.view.title);
    }

    const handleDatesSet = (info: { startStr: string; endStr: string; }) => {
        setStartDate(moment(info.startStr).valueOf());
        setEndDate(moment(info.endStr).valueOf());
        updateTitle();
    };

    const [language, setLanguage] = useState('pt-br');

    async function getCalendarEvents() {
        try {
            const calendarDates = await centralAPI.getCalendarEvents({ startDate, endDate });
            setCalendarEvents(calendarDates);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    useEffect(() => {
        if (startDate && endDate) {
            getCalendarEvents();
        }
    }, [startDate, endDate]);

    useEffect(() => {
        getLanguage();
    }, []);

    async function getLanguage() {
        const language = await getDefaultLanguage();
        setLanguage(language);
    }

    function ViewSelector() {
        return <View style={styles.headerGroup}>
            <CustomButton
                type='secondary'
                text={translate('month')}
                isActive={currentView === 'dayGridMonth'}
                onPress={() => {
                    setCurrentView('dayGridMonth');
                    calendarRef.current?.getApi().changeView('dayGridMonth');
                }}
            />
            <CustomButton
                type='secondary'
                text={translate('week')}
                isActive={currentView === 'timeGridWeek'}
                onPress={() => {
                    setCurrentView('timeGridWeek');
                    calendarRef.current?.getApi().changeView('timeGridWeek');
                }}
            />
            <CustomButton
                type='secondary'
                text={translate('day')}
                isActive={currentView === 'timeGridDay'}
                onPress={() => {
                    setCurrentView('timeGridDay');
                    calendarRef.current?.getApi().changeView('timeGridDay');
                }}
            />
        </View>;
    }

    function Title() {
        return <View style={styles.headerGroup}>
            <MyAppText style={{
                fontSize: 20,
                fontWeight: 'bold',
                textAlign: 'center',
            }}>{titleCasePt(title)}</MyAppText>
        </View>;
    }

    function Directions(props: { showToday: boolean; }) {
        return <View style={styles.headerGroup}>
            <CustomButton
                type='secondary'
                icon={faChevronLeft}
                onPress={() => {
                    calendarRef.current?.getApi().prev();

                }}
            />
            <CustomButton
                type='secondary'
                icon={faChevronRight}
                onPress={() => {
                    calendarRef.current?.getApi().next();
                }}
            />
            {props.showToday &&
                <CustomButton
                    type='secondary'
                    text={translate('today')}
                    onPress={() => {
                        calendarRef.current?.getApi().today();
                    }}
                />
            }
        </View>;
    }

    return <Container>
        <Header
            title={translate('calendar')}
        />
        <Card style={{ rowGap: 15 }}>
            {windowInfo.isMobile ?
                <View style={{ alignSelf: 'center', gap: 4 }}>
                    <ViewSelector />
                    <Directions showToday={false} />
                    <Title />
                </View>
                : <View style={{ justifyContent: 'space-between', gap: 4, flexDirection: 'row' }}>
                    <Directions showToday={true} />
                    <Title />
                    <ViewSelector />
                </View>
            }
            <FullCalendar
                height='100%'
                locales={language == 'pt-br' ? [ptBrLocale] : []}
                locale={language == 'pt-br' ? 'pt-br' : undefined}
                allDaySlot={true}
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin]}
                initialView='timeGridWeek'
                headerToolbar={false}
                eventContent={(arg) => {
                    if (arg.view.type === 'dayGridMonth') {
                        return <View style={{
                            backgroundColor: arg.backgroundColor,
                            width: '100%',
                            minHeight: 22,
                            borderRadius: 3,
                            overflow: 'hidden',
                        }}>
                            <MyAppText style={styles.monthEventText(arg.textColor)}>{arg.event.title}</MyAppText>
                        </View>;
                    }
                    return <View style={{
                        overflow: 'hidden',
                    }}>
                        <MyAppText style={{
                            color: arg.textColor,
                            fontSize: 10,
                        }}>{arg.timeText}</MyAppText>
                        <MyAppText style={styles.weekAndDayEventText(arg.textColor)}>{arg.event.title}</MyAppText>
                    </View>;
                }}
                dayHeaderContent={(arg) => {

                    if (arg.view.type === 'dayGridMonth') {
                        return <div>{arg.text}</div>;
                    } else {
                        const isToday = arg.isToday;

                        return <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: 8 }}>{arg.date.toLocaleDateString('pt-BR', { weekday: 'short' })}</span>
                            <View style={[{
                                width: 28,
                                height: 28,
                                alignContent: 'center',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }, isToday ? {
                                backgroundColor: '#2563eb',
                                borderRadius: 9999,
                            } : {}]}>
                                <MyAppText style={[isToday ? {
                                    color: '#ffffff',
                                } : {}]}>{arg.date.getDate()}</MyAppText>
                            </View>
                        </div>;
                    }

                }}
                datesSet={handleDatesSet}
                weekends={true}
                events={[
                    ...(calendarEvents?.holidays?.map(h => ({
                        title: h.title,
                        start: h.startDate,
                        end: h.endDate,
                        allDay: h.allDay,
                        color: UnistylesRuntime.themeName == 'dark' ? '#204935' : '#d0eadc',
                        textColor: UnistylesRuntime.themeName == 'dark' ? '#20b06d' : '#0b5433'
                    })) ?? []),
                    ...(calendarEvents?.workingIntervals?.map(h => ({
                        title: translate('workingSchedule'),
                        start: h.startDate,
                        end: h.endDate,
                        color: UnistylesRuntime.themeName == 'dark' ? '#543331' : '#f4dddc',
                        textColor: UnistylesRuntime.themeName == 'dark' ? '#c96860' : '#65332f'

                    })) ?? []),
                    ...(calendarEvents?.deacs?.map(h => ({
                        title: `${translate('deac')} - ${h.title}`,
                        start: h.startDate,
                        end: h.endDate,
                        color: UnistylesRuntime.themeName == 'dark' ? '#455a5b' : '#d8e6eb',
                        textColor: UnistylesRuntime.themeName == 'dark' ? '#e5e8e7' : '#262b2b'
                    })) ?? []),
                    ...(calendarEvents?.absence?.map(h => ({
                        title: translate('absence'),
                        start: h.startDate,
                        end: moment(h.endDate).add(1, 'second').toISOString(),
                        allDay: true,
                        color: UnistylesRuntime.themeName == 'dark' ? '#1d4962' : '#d1ebfd',
                        textColor: UnistylesRuntime.themeName == 'dark' ? '#19abf7' : '#185b80'
                    })) ?? []),
                    ...(calendarEvents?.vacation?.map(h => ({
                        title: translate('vacation'),
                        start: h.startDate,
                        end: moment(h.endDate).add(1, 'second').toISOString(),
                        allDay: true,
                        color: UnistylesRuntime.themeName == 'dark' ? '#53375a' : '#f2def8',
                        textColor: UnistylesRuntime.themeName == 'dark' ? '#b567c7' : '#5a3364'
                    })) ?? []),
                    ...(calendarEvents?.beforeAfterIntervals?.map(h => ({
                        title: translate('workingInterval'),
                        start: h.startDate,
                        end: h.endDate,
                        color: UnistylesRuntime.themeName == 'dark' ? '#455a5b' : '#e2e6ec',
                        textColor: UnistylesRuntime.themeName == 'dark' ? '#e5e8e7' : '#414a58'
                    })) ?? [])
                ]}
            />
        </Card>
    </Container>;

}

const stylesheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        rowGap: 15,
    },
    cardList: {
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.container.border,
        backgroundColor: theme.colors.container.background,
        flex: 1,
        padding: 24,

        // full calendar css variables
        '--fc-border-color': theme.colors.container.border,
        '--fc-page-bg-color': theme.colors.container.border,
        '--fc-neutral-text-color': theme.colors.textColor,

        '--fc-button-text-color': theme.colors.secondaryButton.text,
        '--fc-button-bg-color': theme.colors.secondaryButton.background,
        '--fc-button-border-color': theme.colors.secondaryButton.borderColor,
        '--fc-button-hover-bg-color': theme.colors.secondaryButton.hoverBackground,
        '--fc-button-hover-border-color': theme.colors.secondaryButton.hoverBorderColor,

        '--fc-button-active-bg-color': theme.colors.secondaryButton.activeBackgroundColor,
        '--fc-button-active-border-color': theme.colors.secondaryButton.borderColor,

        '--fc-event-hover-color': theme.colors.secondaryButton.activeBackgroundColor,

    },
    monthEventText: (textColor: string) => ({
        color: textColor,
        whiteSpace: 'nowrap',
    }),
    weekAndDayEventText: (textColor: string) => ({
        color: textColor,
        whiteSpace: 'normal',
        wordBreak: 'keep-all',
        overflowWrap: 'normal',
    }),
    headerGroup: {
        flexDirection: 'row',
        alignSelf: 'center',
        gap: 2,
    }
}));
